import React, { useState, useEffect } from 'react'
import defaultClasses from '@corratech/pylot-rich-content/src/PageBuilder/ContentTypes/Products/products.module.scss'
import { mergeClasses } from '@corratech/pylot-rich-content/src/PageBuilder/classify'
import { arrayOf, bool, number, oneOf, shape, string, object } from 'prop-types'
import dynamic from 'next/dynamic'
const SlickSlider = dynamic(() => import('react-slick'))
import Gallery from '@corratech/pylot-rich-content/src/Gallery'
import { useQuery } from '@pylot-data/hooks/use-query'
import { GET_PRODUCTS_BY_SKU_QUERY } from '@components/alfakher/pylot-rich-content/src/Queries/getProductsBySku'
import GalleryItem from '@corratech/pylot-rich-content/src/Gallery/item'
import Skeleton from 'react-loading-skeleton'
import cn from 'classnames'
import slickStyles from '@corratech/pylot-rich-content/src/PageBuilder/ContentTypes/Products/SlickExtend.module.scss'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { getPDPUrl } from '@lib/getPDPUrl'
import { getFastlyUrl } from '@corratech/pylot-image'
import Link from 'next/link'
import { useDeviceType } from '@components/common/IsMobile/useDeviceType'
import { useIsArabic } from '@components/common/IsArabic/useIsArabic'

/**
 * Sort products based on the original order of SKUs
 *
 * @param {Array} skus
 * @param {Array} products
 * @returns {Array}
 */
const restoreSortOrder = (skus, products) => {
    const productsBySku = new Map()
    products?.forEach((product) => {
        productsBySku.set(product.sku, product)
    })
    return skus.map((sku) => productsBySku.get(sku)).filter(Boolean)
}

// map Magento 2.3.1 schema changes to Venia 2.0.0 proptype shape to maintain backwards compatibility
const mapGalleryItem = (item) => {
    const { small_image } = item
    return {
        ...item,
        small_image:
            typeof small_image === 'object' ? small_image.url : small_image
    }
}

const LoaderTile = () => (
    <div className={cn(defaultClasses['productitem-skeleton'])}>
        <Skeleton
            height={320}
            width={320}
            className={defaultClasses['product-skeleton-img']}
        />
        <Skeleton
            height={320}
            width={320}
            className={defaultClasses['product-skeleton-img']}
        />
        <Skeleton
            height={320}
            width={320}
            className={defaultClasses['product-skeleton-img']}
        />
        <Skeleton
            height={320}
            width={320}
            className={defaultClasses['product-skeleton-img']}
        />
    </div>
)

/**
 * Page Builder Products component.
 *
 * This component is part of the Page Builder / PWA integration. It can be consumed without Page Builder.
 *
 * @typedef Products
 * @kind functional component
 *
 * @param {props} props React component props
 *
 * @returns {React.Element} A React component that displays a Products based on a number of skus.
 */
const Products = (props) => {
    const { isMobile, isTablet } = useDeviceType()
    // const [t] = useTranslation();
    const t = (string) => string // NOSONAR
    const classes = mergeClasses(defaultClasses, props.classes)
    const {
        appearance,
        autoplay,
        autoplaySpeed,
        infinite,
        arrows,
        dots,
        draggable,
        carouselMode,
        centerPadding,
        skus,
        textAlign,
        border,
        borderColor,
        borderWidth,
        borderRadius,
        marginTop,
        marginRight,
        marginBottom,
        marginLeft,
        paddingTop,
        paddingRight,
        paddingBottom,
        paddingLeft,
        cssClasses = [],
        slidesToShow = 3.5,
        slideToShowSmall = 1.4,
        slideToShowSmallCenterMode = 1,
        AddToWishlist,
        AddToWishlistLabel,
        ProductTile,
        productQuery,
        globalOptions = {},
        imageQuality
    } = props

    const dynamicStyles = {
        textAlign,
        border,
        borderColor,
        borderWidth,
        borderRadius,
        marginTop,
        marginRight,
        marginBottom,
        marginLeft,
        paddingTop,
        paddingRight,
        paddingBottom,
        paddingLeft
    }

    const isArabic = useIsArabic()

    const renderNoProducts = () => {
        return (
            <div
                style={dynamicStyles}
                className={[classes.root, ...cssClasses].join(' ')}
            >
                <div className={classes.error}>
                    {t('No products to display')}
                    {skus.length ? (
                        <>
                            {t('Should be requested products with SKUs')}{' '}
                            {skus.join(', ')}
                        </>
                    ) : (
                        ''
                    )}
                </div>
            </div>
        )
    }

    const { data, error } = useQuery(
        productQuery,
        { skus },
        {
            revalidateOnFocus: false
        }
    )

    if (!skus?.length) return renderNoProducts()

    if (!data && !error) return LoaderTile()

    if (
        error ||
        !data?.data?.products ||
        data?.data?.products?.items?.length === 0
    )
        return renderNoProducts()

    const items = restoreSortOrder(skus, data.data.products.items)

    let brandThemeClass = 'theme-light'
    if (props?.brandTheme === 'theme-dark') {
        brandThemeClass = 'theme-dark'
    }

    if (cssClasses.includes('tacony-schematic-parts-list')) {
        return items.length > 0 ? (
            <ol className="parts-list">
                {items.map((item, index) => {
                    return (
                        <li key={index}>
                            <a href={getPDPUrl(item.url_key)}>
                                {item.name} #{item.sku}
                            </a>
                        </li>
                    )
                })}
            </ol>
        ) : null
    }

    if (appearance === 'carousel') {
        const galleryItems = items.map((item, index) => {
            return ProductTile ? (
                <ProductTile product={item} className="sliderItem" />
            ) : (
                <GalleryItem key={index} item={mapGalleryItem(item)} />
            )
        })

        if (isMobile) {
            return (
                <PerfectScrollbar style={{ direction: 'ltr' }}>
                    <div
                        className={[
                            classes.carousel,
                            ...cssClasses,
                            'mobile-scrollbar',
                            defaultClasses['mobile-scrollbar']
                        ].join(' ')}
                    >
                        {galleryItems}
                    </div>
                </PerfectScrollbar>
            )
        }
        //Settings conditions was made due to react-slick issues
        const carouselCenterMode =
            carouselMode === 'continuous' && items.length > slidesToShow
        const carouselSmallCenterMode =
            carouselMode === 'continuous' &&
            items.length > slideToShowSmallCenterMode
        let carouselSettings = {
            slidesToShow: slidesToShow,
            slidesToScroll: slidesToShow,
            draggable,
            autoplay,
            autoplaySpeed,
            arrows,
            dots,
            centerMode: carouselCenterMode,
            responsive: [
                {
                    breakpoint: 1921,
                    settings: {
                        slidesToShow: 5.1,
                        slidesToScroll: 1
                    }
                },
                {
                    breakpoint: 1600,
                    settings: {
                        slidesToShow: 4.2,
                        slidesToScroll: 1
                    }
                },
                {
                    breakpoint: 1367,
                    settings: {
                        slidesToShow: 3.65,
                        slidesToScroll: 1
                    }
                },
                {
                    breakpoint: 1024,
                    settings: {
                        slidesToShow: 3.2,
                        slidesToScroll: 1
                    }
                },
                {
                    breakpoint: 820,
                    settings: {
                        slidesToShow: 2.6,
                        slidesToScroll: 1
                    }
                },
                {
                    breakpoint: 640,
                    settings: {
                        slidesToShow: carouselSmallCenterMode
                            ? slideToShowSmallCenterMode
                            : slideToShowSmall,
                        slidesToScroll: carouselSmallCenterMode
                            ? slideToShowSmallCenterMode
                            : 1,
                        centerMode: carouselSmallCenterMode,
                        ...(carouselSmallCenterMode && { centerPadding }),
                        ...{
                            infinite:
                                items.length > slideToShowSmall && infinite
                        },
                        dots: true,
                        arrows: false
                    }
                }
            ],
            ...(carouselCenterMode && { centerPadding }),
            ...{ infinite: items.length > slidesToShow && infinite }
        }

        const centerModeClass = carouselCenterMode ? classes.centerMode : null
        const centerModeSmallClass = carouselSmallCenterMode
            ? classes.centerModeSmall
            : null

        let extraConfig = {}

        try {
            extraConfig = JSON.parse(props.additionalConfig)
        } catch (e) {
            console.warn('Additional Slider Configuration JSON format issue')
        }

        if (extraConfig) {
            carouselSettings = { ...carouselSettings, ...extraConfig }
        }

        return (
            <div
                style={dynamicStyles}
                className={[
                    classes.carousel,
                    ...cssClasses,
                    centerModeClass,
                    centerModeSmallClass,
                    defaultClasses['product-slider-default']
                ].join(' ')}
            >
                <SlickSlider
                    {...carouselSettings}
                    className={cn(slickStyles.root)}
                >
                    {galleryItems}
                </SlickSlider>
            </div>
        )
    }

    if (appearance === 'carousel_image') {
        const galleryItems = items.map((item, index) => {
            return ProductTile ? (
                <ProductTile product={item} className="sliderItem" />
            ) : (
                <GalleryItem key={index} item={mapGalleryItem(item)} />
            )
        })

        if (isMobile || isTablet) {
            return (
                <div
                    className={
                        defaultClasses['product-slider-with-image-wrapper']
                    }
                >
                    <figure
                        className={
                            defaultClasses['brand-img-with-carousel-wrapper']
                        }
                    >
                        <picture>
                            <source
                                media="(max-width: 767px)"
                                srcSet={props?.brandBgMobile}
                            />
                            <img
                                className={
                                    defaultClasses['brand-img-with-carousel']
                                }
                                src={getFastlyUrl({
                                    src: props?.brandBg,
                                    quality: imageQuality
                                })}
                                alt={props?.brandBg}
                                loading="lazy"
                            />
                        </picture>
                    </figure>
                    <div className={defaultClasses['mobile-scrollbar-wrapper']}>
                        <div
                            className={cn([
                                defaultClasses['brand-content-wrapper-inner'],
                                defaultClasses[brandThemeClass]
                            ])}
                        >
                            <p className={defaultClasses['brand-content-text']}>
                                {props?.brandContent}
                            </p>
                            {props.brandLogo ? (
                                <img
                                    className={defaultClasses['brand-logo']}
                                    src={props?.brandLogo}
                                    alt={props?.brandLogo}
                                    loading="lazy"
                                />
                            ) : (
                                <h2 className={defaultClasses['brand-heading']}>
                                    {props?.brandHeading}
                                </h2>
                            )}
                            <div
                                className={
                                    defaultClasses[
                                        'brand-view-all-button-wrapper'
                                    ]
                                }
                            >
                                {props?.brandButtonText &&
                                    props?.brandButtonLink && (
                                        <Link href={props?.brandButtonLink}>
                                            <a
                                                className={
                                                    defaultClasses[
                                                        'brand-view-all-button'
                                                    ]
                                                }
                                            >
                                                {props?.brandButtonText}
                                            </a>
                                        </Link>
                                    )}
                            </div>
                        </div>
                        {props.isProductGridEnabledOnMobile !== 'false' ? (
                            <Gallery
                                AddToWishlist={AddToWishlist}
                                AddToWishlistLabel={AddToWishlistLabel}
                                items={items}
                                classes={{ items: classes.galleryItems }}
                                globalOptions={globalOptions}
                                ProductTile={ProductTile}
                            />
                        ) : (
                            <PerfectScrollbar
                                style={
                                    isArabic
                                        ? { direction: 'rtl' }
                                        : { direction: 'ltr' }
                                }
                            >
                                <div
                                    className={[
                                        classes.carousel,
                                        ...cssClasses,
                                        'mobile-scrollbar',
                                        defaultClasses['mobile-scrollbar']
                                    ].join(' ')}
                                >
                                    {galleryItems}
                                </div>
                            </PerfectScrollbar>
                        )}
                    </div>
                </div>
            )
        }

        //Settings conditions was made due to react-slick issues
        const carouselCenterMode =
            carouselMode === 'continuous' && items.length > slidesToShow
        const carouselSmallCenterMode =
            carouselMode === 'continuous' &&
            items.length > slideToShowSmallCenterMode
        let carouselSettings = {
            slidesToShow: 4,
            slidesToScroll: 1,
            draggable: true,
            swipe: true,
            touchMove: true,
            autoplay,
            autoplaySpeed,
            variableWidth: true,
            rtl: isArabic,
            arrows,
            dots,
            centerMode: carouselCenterMode,
            responsive: [
                {
                    breakpoint: 1921,
                    settings: {
                        slidesToShow: 4,
                        slidesToScroll: 1
                    }
                },
                {
                    breakpoint: 1600,
                    settings: {
                        slidesToShow: 4,
                        slidesToScroll: 1
                    }
                },
                {
                    breakpoint: 1367,
                    settings: {
                        slidesToShow: 4,
                        slidesToScroll: 1
                    }
                },
                {
                    breakpoint: 1024,
                    settings: {
                        slidesToShow: 4,
                        slidesToScroll: 1
                    }
                },
                {
                    breakpoint: 820,
                    settings: {
                        slidesToShow: 2.6,
                        slidesToScroll: 1
                    }
                },
                {
                    breakpoint: 640,
                    settings: {
                        slidesToShow: carouselSmallCenterMode
                            ? slideToShowSmallCenterMode
                            : slideToShowSmall,
                        slidesToScroll: carouselSmallCenterMode
                            ? slideToShowSmallCenterMode
                            : 1,
                        centerMode: carouselSmallCenterMode,
                        ...(carouselSmallCenterMode && { centerPadding }),
                        ...{
                            infinite:
                                items.length > slideToShowSmall && infinite
                        },
                        dots: true,
                        arrows: false
                    }
                }
            ],
            ...(carouselCenterMode && { centerPadding }),
            ...{
                infinite: items.length > slidesToShow && infinite,
                initialSlide: isArabic
                    ? Math.min(3, galleryItems.length - 1)
                    : 0
            }
        }

        const centerModeClass = carouselCenterMode ? classes.centerMode : null
        const centerModeSmallClass = carouselSmallCenterMode
            ? classes.centerModeSmall
            : null

        let extraConfig = {}

        try {
            extraConfig = JSON.parse(props.additionalConfig)
        } catch (e) {
            console.warn('Additional Slider Configuration JSON format issue')
        }

        if (extraConfig) {
            carouselSettings = { ...carouselSettings, ...extraConfig }
        }

        return (
            <div
                className={defaultClasses['product-slider-with-image-wrapper']}
            >
                <figure
                    className={
                        defaultClasses['brand-img-with-carousel-wrapper']
                    }
                >
                    <picture>
                        <source
                            media="(max-width: 767px)"
                            srcSet={props?.brandBgMobile}
                        />
                        <img
                            className={
                                defaultClasses['brand-img-with-carousel']
                            }
                            src={getFastlyUrl({
                                src: props?.brandBg,
                                quality: imageQuality
                            })}
                            alt={props?.brandBg}
                            loading="lazy"
                        />
                    </picture>
                </figure>
                <div
                    style={dynamicStyles}
                    className={[
                        classes.carousel,
                        ...cssClasses,
                        centerModeClass,
                        centerModeSmallClass,
                        defaultClasses['product-slider-with-image-default']
                    ].join(' ')}
                >
                    <div
                        className={cn([
                            defaultClasses['brand-content-wrapper-inner'],
                            defaultClasses[brandThemeClass]
                        ])}
                    >
                        <p className={defaultClasses['brand-content-text']}>
                            {props?.brandContent}
                        </p>
                        {props.brandLogo ? (
                            <img
                                className={defaultClasses['brand-logo']}
                                src={props?.brandLogo}
                                alt={props?.brandLogo}
                                loading="lazy"
                            />
                        ) : (
                            <h2 className={defaultClasses['brand-heading']}>
                                {props?.brandHeading}
                            </h2>
                        )}
                        <div
                            className={
                                defaultClasses['brand-view-all-button-wrapper']
                            }
                        >
                            {props?.brandButtonText && props?.brandButtonLink && (
                                <Link href={props?.brandButtonLink}>
                                    <a
                                        className={
                                            defaultClasses[
                                                'brand-view-all-button'
                                            ]
                                        }
                                    >
                                        {props?.brandButtonText}
                                    </a>
                                </Link>
                            )}
                        </div>
                    </div>
                    {props.isProductGridEnabledOnDesktop === 'true' ? (
                        <Gallery
                            AddToWishlist={AddToWishlist}
                            AddToWishlistLabel={AddToWishlistLabel}
                            items={items}
                            classes={{ items: classes.galleryItems }}
                            globalOptions={globalOptions}
                            ProductTile={ProductTile}
                        />
                    ) : (
                        <div dir={isArabic ? 'rtl' : 'ltr'}>
                            <SlickSlider
                                {...carouselSettings}
                                className={cn(slickStyles.root)}
                            >
                                {galleryItems}
                            </SlickSlider>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    return (
        <div
            style={dynamicStyles}
            className={[classes.root, ...cssClasses].join(' ')}
        >
            <Gallery
                AddToWishlist={AddToWishlist}
                AddToWishlistLabel={AddToWishlistLabel}
                items={items}
                classes={{ items: classes.galleryItems }}
                globalOptions={globalOptions}
                ProductTile={ProductTile}
            />
        </div>
    )
}

/**
 * Props for {@link Products}
 *
 * @typedef props
 *
 * @property {Object} classes An object containing the class names for the Products
 * @property {String} classes.root CSS class for products
 * @property {String} classes.carousel CSS class for products carousel appearance
 * @property {String} classes.centerMode CSS class for products carousel appearance with center mode
 * @property {String} classes.centerModeSmall CSS class for products carousel appearance with center mode on small screen
 * @property {String} classes.galleryItems CSS class to modify child gallery items
 * @property {String} classes.error CSS class for displaying fetch errors
 * @property {String} appearance Sets products appearance
 * @property {Boolean} autoplay Whether the carousel should autoplay
 * @property {Number} autoplaySpeed The speed at which the autoplay should move the slide on
 * @property {Boolean} infinite Whether to infinitely scroll the carousel
 * @property {Boolean} arrows Whether to show arrows on the slide for navigation
 * @property {Boolean} dots Whether to show navigation dots at the bottom of the carousel
 * @property {Boolean} draggable Enable scrollable via dragging on desktop
 * @property {String} carouselMode Carousel mode
 * @property {String} centerPadding Horizontal padding in centerMode
 * @property {Array} skus List of SKUs to load into product list
 * @property {String} textAlign Alignment of content within the products list
 * @property {String} border CSS border property
 * @property {String} borderColor CSS border color property
 * @property {String} borderWidth CSS border width property
 * @property {String} borderRadius CSS border radius property
 * @property {String} marginTop CSS margin top property
 * @property {String} marginRight CSS margin right property
 * @property {String} marginBottom CSS margin bottom property
 * @property {String} marginLeft CSS margin left property
 * @property {String} paddingTop CSS padding top property
 * @property {String} paddingRight CSS padding right property
 * @property {String} paddingBottom CSS padding bottom property
 * @property {String} paddingLeft CSS padding left property
 * @property {Array} cssClasses List of CSS classes to be applied to the component
 * @property {Number} slidesToShow # of slides to show at a time
 * @property {Number} slidesToShowSmall # of slides to show at a time on small screen
 * @property {Number} slidesToShowSmallCenterMode # of slides to show at a time on small screen in centerMode
 */
Products.propTypes = {
    classes: shape({
        root: string,
        carousel: string,
        centerMode: string,
        centerModeSmall: string,
        galleryItems: string,
        error: string
    }),
    appearance: oneOf(['grid', 'carousel']),
    autoplay: bool,
    autoplaySpeed: number,
    infinite: bool,
    arrows: bool,
    dots: bool,
    draggable: bool,
    carouselMode: oneOf(['default', 'continuous']),
    centerPadding: string,
    skus: arrayOf(string),
    textAlign: string,
    border: string,
    borderColor: string,
    borderWidth: string,
    borderRadius: string,
    marginTop: string,
    marginRight: string,
    marginBottom: string,
    marginLeft: string,
    paddingTop: string,
    paddingRight: string,
    paddingBottom: string,
    paddingLeft: string,
    cssClasses: arrayOf(string),
    slidesToShow: number,
    slidesToShowSmall: number,
    slidesToShowSmallCenterMode: number,
    globalOptions: object
}

Products.defaultProps = {
    productQuery: GET_PRODUCTS_BY_SKU_QUERY
}

export default Products
